"use client";
import { useLayoutEffect, useRef, useState } from "react";
import { Icon, IconName } from "@zapier/zinnia-icons";
import {
  Img,
  ServiceIcons,
  Tag,
  Tooltip,
  TooltipFocusableTrigger,
} from "@zapier/design-system";
import classNames from "classnames";
import {
  TemplateBookmarkButton,
  type TemplateBookmarkActions,
} from "src/components/TemplateBookmarkButton";

import { type Template } from "src/types";

import {
  emitUserInteractionEvent,
  UserInteractionEventName,
} from "src/observability/tracking";

import styles from "./TemplateCard.module.css";
import { useAppDetailsBySlugs } from "src/hooks";
import { useInView } from "react-intersection-observer";
import { filterBuiltInApps } from "src/utils";

enum TEMPLATE_CARD_CLICK_EVENT_INTERACTION_GOALS {
  showcase_feature = "showcase_feature",
  search = "search",
}

export type TemplateCardClickEventInteractionGoal =
  keyof typeof TEMPLATE_CARD_CLICK_EVENT_INTERACTION_GOALS;

export type TemplateCardSize = "small" | "large" | "fullWidth" | "responsive";

type Props = {
  /** The size variant of the card. Can be 'small', 'large', or 'fullWidth'. Defaults to 'small'. */
  size?: TemplateCardSize;
  /**
   * The template data to display in the card.
   *
   * The expected properties are the API response from the `/api/templates/v1/templates` endpoint, in addition the following optional fields:
   * - webIntentUrl: the webintent URL for the template
   * - zapBreakdownDetails: the zap breakdown details for the template
   *
   *
   * [Template API reference](https://gitlab.com/zapier/team-sprout/templatesbackend/-/blob/main/openapi.yaml)
   */
  template: Template;
  /** The href for the template card */
  href?: string;
  /** Whether the user is logged in */
  isLoggedIn: boolean;
  /** The interaction goal for click event tracking */
  clickEventInteractionGoal?: TemplateCardClickEventInteractionGoal;
  /** Actions for the template bookmark button: save, unsave, isBookmarked, isLoading */
  templateBookmarkActions?: TemplateBookmarkActions;
  /** Optional click handler for the card */
  onClick?: (() => void) | null;
  /** Additional CSS classes to apply to the card */
  className?: string;
  /** Whether the template has bookmarking */
  hasBookmarking: boolean;
  /** Optionally render a Tag in the top left of the card */
  tagLabel?: string;
  /** Allows consumer to render the image in the card (allows for use of next/image by consumer) */
  renderedImage?: JSX.Element;
  /** Whether to show the product tags on the card */
  showProductTags?: boolean;
  /** The base URL for the app details */
  baseUrl?: string;
  /** The API host name */
  apiHostName?: string;
};

const LOGGED_IN_VARIANT_KEY = "logged-in";

export const TemplateCard = ({
  href,
  isLoggedIn,
  size = "small",
  template,
  clickEventInteractionGoal,
  onClick,
  templateBookmarkActions,
  className,
  hasBookmarking,
  tagLabel,
  renderedImage,
  showProductTags = true,
  apiHostName,
}: Props) => {
  const apps = template.apps || [];
  const uniqueServiceNames = Array.from(new Set(apps));

  // Deduplicate products by slug to prevent React key conflicts
  const products = Array.from(
    new Map((template.products ?? []).map((p) => [p.slug, p])).values(),
  );

  const isZapTemplate =
    products.length === 1 &&
    products[0]?.slug === "zaps" &&
    uniqueServiceNames.length > 0;

  const shouldRenderAppIcons = isZapTemplate && !template.previewImage?.url;

  const { inView, ref } = useInView({
    threshold: 0.1,
  });

  const { data: appDetails } = useAppDetailsBySlugs({
    slugs: uniqueServiceNames,
    skip: !shouldRenderAppIcons || (shouldRenderAppIcons && !inView),
  });

  // Filter out built-in apps (e.g., "Filter by Zapier", "Webhooks by Zapier")
  const filteredServiceNames = filterBuiltInApps(
    uniqueServiceNames,
    appDetails,
  );

  const variant = isLoggedIn ? LOGGED_IN_VARIANT_KEY : null;

  // Priority: nameMechanical > variant name > regular name
  const variantName = variant
    ? template.variants?.find((v) => v.key === variant)?.name
    : undefined;
  const name = template.nameMechanical || variantName || template.name;

  const titleRef = useRef<HTMLSpanElement>(null);
  const [isTitleTruncated, setIsTitleTruncated] = useState(false);

  useLayoutEffect(() => {
    const element = titleRef.current;
    if (!element) return;

    const checkTruncation = () => {
      setIsTitleTruncated(element.scrollHeight > element.clientHeight);
    };

    const animationFrameId = requestAnimationFrame(checkTruncation);

    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  const handleTemplateClick = () => {
    emitUserInteractionEvent(UserInteractionEventName.templateCardClicked, {
      event_action: "click",
      option_selected: template.slug,
      ...(clickEventInteractionGoal
        ? { interaction_goal: clickEventInteractionGoal }
        : {}),
      custom_tracking_fields: {
        template_id: template.id,
      },
    });

    if (onClick && typeof onClick === "function") {
      onClick();
    }
  };

  const cardTitle = (
    <span
      ref={titleRef}
      className={classNames(styles.title, {
        [styles.titleWithProductTags]: showProductTags,
      })}
    >
      {name}
    </span>
  );

  const renderCardContent = () => (
    <>
      <div className={styles.topOfCard}>
        {shouldRenderAppIcons ? (
          <>
            <span className={styles.serviceIconWrapper} ref={ref}>
              <Tooltip
                allowMultiline={true}
                content={filteredServiceNames
                  .map((service) => appDetails?.get(service)?.name ?? service)
                  .join(", ")}
                placement="bottom left"
              >
                <TooltipFocusableTrigger>
                  <ServiceIcons
                    aria-label={`Zap uses ${filteredServiceNames.join(", ")}`}
                    services={filteredServiceNames.map((service) => ({
                      name: appDetails?.get(service)?.name ?? service,
                      src: `https://zapier.com/generated/${encodeURIComponent(service)}/64`,
                    }))}
                    size="medium"
                    maxServices={3}
                  />
                </TooltipFocusableTrigger>
              </Tooltip>
            </span>
          </>
        ) : renderedImage ? (
          <div className={styles.imageContainer}>{renderedImage}</div>
        ) : (
          <Img
            src={
              template.previewImage?.url
                ? `${apiHostName || ""}${template.previewImage?.url}`
                : "https://images.ctfassets.net/0sppvm4cmdq7/42L9dKclk2wI6gI1Lgas4K/020b75d5c6590d7b4ea21e78a3f6e690/GenericThumbnail.png"
            }
            alt={template.previewImage?.alt ?? ""}
            objectFit="cover"
            objectPosition="top left"
            borderRadius="none"
            isFullWidth
          />
        )}
      </div>
      <div className={styles.metadataWrapper}>
        {isTitleTruncated ? (
          <Tooltip
            content={name}
            placement="top left"
            allowMultiline={true}
            style={{ zIndex: 2 }}
          >
            <TooltipFocusableTrigger>{cardTitle}</TooltipFocusableTrigger>
          </Tooltip>
        ) : (
          cardTitle
        )}

        {showProductTags && (
          <span className={styles.tag}>
            <Tooltip
              allowMultiline={true}
              content={products
                .map((product) => product.name ?? product.slug)
                .join(", ")}
              placement="bottom left"
            >
              <TooltipFocusableTrigger>
                <span className={styles.tagIcons}>
                  {products.map((product) => (
                    <Icon
                      key={`key-${product.slug}`}
                      name={product.iconName as IconName}
                      color="BrandOrange"
                      size={14}
                    />
                  ))}
                </span>
              </TooltipFocusableTrigger>
            </Tooltip>

            <span className={styles.tagText} data-testid="products">
              {products.length > 1 ? (
                <>
                  Multi-product
                  <span className={styles.hideOnMobile}> solution</span>
                </>
              ) : products.length === 1 && products[0]?.name ? (
                <>{products[0].name}</>
              ) : null}
            </span>
          </span>
        )}
      </div>
    </>
  );

  return (
    <article
      className={classNames([styles.root, styles[size], className])}
      data-testid="zap-card"
    >
      {tagLabel && (
        <Tag variant="inverted" className={styles.tagLabel}>
          {tagLabel}
        </Tag>
      )}
      {href ? (
        <a
          href={href}
          className={classNames([
            styles.zapCard,
            shouldRenderAppIcons ? styles.withAppIcons : styles.withLargeImage,
          ])}
          aria-label={template.name ?? ""}
          onClick={handleTemplateClick}
        >
          {renderCardContent()}
        </a>
      ) : (
        <button
          className={classNames([
            styles.zapCard,
            shouldRenderAppIcons ? styles.withAppIcons : styles.withLargeImage,
          ])}
          aria-label={template.name ?? ""}
          onClick={handleTemplateClick}
        >
          {renderCardContent()}
        </button>
      )}
      {isLoggedIn &&
        hasBookmarking &&
        templateBookmarkActions &&
        template.id && (
          <div className={styles.bookmarkButtonContainer}>
            <TemplateBookmarkButton
              isZapTemplate={isZapTemplate}
              id={template.id}
              slug={template.slug}
              templateBookmarkActions={templateBookmarkActions}
            />
          </div>
        )}
    </article>
  );
};
